/**
 * drilldownSubTabs — the sub-views that live behind a primary drilldown tab.
 *
 * A few primary tabs are really a small family of views under one nav entry:
 * Documents has its sub-header strip (Uploaded / Parsed / Ocrolus / Missing),
 * Wire & Funding has its four route-driven panes. This registry maps a primary
 * tab's path to that family so `LoanDrilldownRouterTabSwitcher` can offer a
 * hover flyout that jumps straight into one of them. Clicking the primary tab
 * itself is unchanged — it still lands on the tab's own default view.
 *
 * Each entry is `{ key, label, icon, to }`, where `to` is relative to the loan
 * base url (`/loan/:id/`). Deliberately NO counts: the flyout is a jump list,
 * and the real sub-header is where the numbers belong.
 *
 * An entry may instead carry `{ sidebarType, sidebarState? }` and NO `to` — an
 * ACTION row, which opens that drilldown sidebar where the user already is
 * rather than navigating (Contacts → "Add contact"). The switcher dispatches
 * it; nothing about the tab leaks into the nav.
 *
 * Adding a tab here is the whole integration — the switcher reads this and
 * renders the caret + flyout automatically. The only requirement is that the
 * destination be reachable by URL (a sub-path, or a query param the tab reads).
 */
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import GppGoodOutlined from "@mui/icons-material/GppGoodOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import PersonAddOutlined from "@mui/icons-material/PersonAddOutlined";
import RuleOutlined from "@mui/icons-material/RuleOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";

import { loanDrilldownSidebarTypes } from "../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  BUDGET_SECTION_PARAM,
  getBudgetDrawsSections,
} from "../tabs/BudgetDrawsTab/budgetDrawsSections";
import { DOC_VIEW_OPTIONS } from "../tabs/LoanDrilldownDocumentsTab/LoanDocumentManagerTable";
import {
  CONTACTS_SECTION_PARAM,
  CONTACT_ACTIONS,
  getContactsSections,
} from "../tabs/LoanDrilldownLoanRelatedContactsTab/contactsSections";
import { PANES } from "../tabs/LoanDrilldownWireAndFundingTab/FundingWorkRail";

/**
 * LEGACY query param (`documents?view=<key>`). Documents views are sub-routes
 * now (`documents/<path>`); the tab still accepts an old `?view=` link and
 * redirects it to the matching sub-route.
 */
export const DOCUMENTS_VIEW_PARAM = "view";

// Wire & Funding: the rail's four artifacts, in lifecycle order. Titles mirror
// FundingWorkRail's rail items (which build theirs inline alongside live
// status), so keep the two in step if a pane is renamed.
const WIRE_AND_FUNDING_PANES = [
  {
    key: PANES.STATEMENT,
    label: "Funding Statement",
    icon: DescriptionOutlined,
  },
  { key: PANES.TIE_OUT, label: "Net Wire Tie-out", icon: RuleOutlined },
  { key: PANES.TICKET, label: "Wire Ticket", icon: ReceiptLongOutlined },
  { key: PANES.SHIELD, label: "Funding Shield", icon: GppGoodOutlined },
];

/**
 * @param {string} tabKey  the primary tab's slug (e.g. "documents")
 * @param {{ isAcquisitions?: boolean, isConstruction?: boolean }} ctx  same
 *        gates the tab itself applies
 * @returns {Array|null} sub-tabs, or null when the tab has none
 */
export const getDrilldownSubTabs = (tabKey, ctx = {}) => {
  // (The Experience Workbook used to be a sub-view under Applicants; it is now
  // the "Experience Calc." primary tab, so Applicants has no flyout.)

  if (tabKey === "documents") {
    // Every view on both pipelines — /checklists/check_missing_documents
    // resolves preclose AND acquisition deals, so Missing is no longer dropped
    // for postclose.
    return DOC_VIEW_OPTIONS.map((o) => ({
      key: o.key,
      label: o.label,
      icon: o.icon,
      to: o.path ? `documents/${o.path}` : "documents",
    }));
  }

  if (tabKey === "contacts") {
    // Contacts is ONE page with three stacked cards, not three routes, so its
    // sub-views are anchors: the flyout deep-links to the section rather than
    // navigating. The list itself lives with the tab (contactsSections.js) so
    // the flyout and the tab's own strip can't drift.
    return [
      // The one thing people come to this tab to DO. No `to`: it opens the
      // add-contact sidebar wherever you already are, rather than moving you
      // to the Contacts tab first to do the same thing.
      {
        key: CONTACT_ACTIONS.ADD_CONTACT,
        label: "Add contact",
        icon: PersonAddOutlined,
        sidebarType: loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT,
      },
      ...getContactsSections({ isConstruction: ctx.isConstruction }).map(
        (section) => ({
          key: section.key,
          label: section.label,
          icon: section.icon,
          to: `contacts?${CONTACTS_SECTION_PARAM}=${section.key}`,
        }),
      ),
    ];
  }

  if (tabKey === "budget-draws") {
    // Like Contacts: ONE page of stacked cards, so the sub-views are anchors
    // and the flyout deep-links to the section rather than navigating. The list
    // lives with the tab (budgetDrawsSections.js). Called with no ctx, so it
    // yields the four permanent sections — Inspections depends on whether any
    // inspection has been extracted, which only the tab can know, and the
    // flyout must not offer a jump to a card that isn't on the page.
    return getBudgetDrawsSections().map((section) => ({
      key: section.key,
      label: section.label,
      icon: section.icon,
      to: `budget-draws?${BUDGET_SECTION_PARAM}=${section.key}`,
    }));
  }

  if (tabKey === "wire-and-funding") {
    return WIRE_AND_FUNDING_PANES.map((p) => ({
      ...p,
      to: `wire-and-funding/${p.key}`,
    }));
  }

  if (tabKey === "closing") {
    // Mirrors the Closing tab's sub-tab switcher (readiness + closing docs);
    // labels branch on the pipeline like the tab does.
    return [
      {
        key: "readiness",
        label: ctx.isAcquisitions ? "Settlement Readiness" : "Readiness",
        icon: RuleOutlined,
        to: "closing/readiness",
      },
      {
        key: "closing-documents",
        label: ctx.isAcquisitions
          ? "Settlement Documents"
          : "Closing Documents",
        icon: DescriptionOutlined,
        to: "closing/closing-documents",
      },
      // The closer's HUD / ALTA workbook is pre-close only (the routes 409
      // on an acquisition) — settlement-workbook.md.
      ...(ctx.isAcquisitions
        ? []
        : [
            {
              key: "settlement-workbook",
              label: "Settlement Workbook",
              icon: TableChartOutlined,
              to: "closing/settlement-workbook",
            },
          ]),
    ];
  }

  return null;
};

export default getDrilldownSubTabs;
